import { Box, Button, VStack, Heading, FormControl, FormLabel, Input, Select, useToast, Text, Flex, Image, HStack, Stack, RadioGroup, Radio, Link, InputGroup, InputLeftElement, InputRightElement, Icon, Checkbox, CheckboxGroup, Center, IconButton, Tooltip, useDisclosure, Popover, PopoverArrow, PopoverTrigger, PopoverContent, PopoverBody, Tag, TagLabel, TagCloseButton, Wrap, WrapItem, Divider } from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useState, useContext, useEffect, useRef } from 'react';
import { useSubmitAccountCreation } from '../hooks/useSubmitAccountCreation';
import { Role } from '../hooks/useValidateSession';
import SessionContext from '../contexts/SessionContext';
import { useSendOTP } from '../hooks/useSendOTP';
import { useVerifyOTP } from '../hooks/useVerifyOTP';
import { useFindOrganization } from '../hooks/useFindOrganization';
import { useCreateOrganization } from '../hooks/useCreateOrganization';
import YaxleLogo from '../assets/yaxleLogoBlue.png';
import { FaEye, FaEyeSlash, FaCheckCircle, FaPlusCircle, FaStore, FaBuilding, FaSitemap, FaQuestionCircle, FaCopy, FaCheck } from 'react-icons/fa';
import PrivacyPolicy from '../modals/PrivacyPolicy';
import { SetupStepper, SetupProgressBar, getOnboardingStepLabel, getSetupStepLabel } from '../components/onboarding/SetupStepper';
import { STEP_FOOTER_PORTAL_ID } from '../components/onboarding/StepFooter';
import { DependencySetupStep } from '../components/onboarding/DependencySetupStep';
import { ITSetupStep } from '../components/onboarding/ITSetupStep';
import axios, { AxiosError } from 'axios';
import API_URL from '../constants/environment';
import { useSearchParams } from 'react-router-dom';
import { CredentialsForm } from '../components/onboarding/CredentialsForm';
import { LocationMappingUI, type CccRowMapping } from '../components/onboarding/LocationMappingUI';
import { MitchellLoginsStep } from '../components/onboarding/MitchellLoginsStep';
import { TeslaLoginsStep } from '../components/onboarding/TeslaLoginsStep';
import { OpsTraxLoginsStep } from '../components/onboarding/OpsTraxLoginsStep';
import { SupplierNetworkStep } from '../components/onboarding/SupplierNetworkStep';
import { InviteUsersStep } from '../components/onboarding/InviteUsersStep';
import { OrgSettingsPresetStep } from '../components/onboarding/OrgSettingsPresetStep';
import { useQueryClient } from '@tanstack/react-query';
import { usePullPartsTraderLocations } from '../hooks/usePullPartsTraderLocations';
import { usePullCccLocations } from '../hooks/usePullCccLocations';
import { useReconcileLocations, type PartstraderLocation, type CccLocation, type LocationMapping } from '../hooks/useReconcileLocations';
import { useCreateTestingLocations } from '../hooks/useCreateTestingLocations';
import { useGetOnboardingStep, useSaveOnboardingStep } from '../hooks/useOnboardingStep';
import { getInvitePositionOptions } from '../utils/invitePositionOptions';

type CreateStep =
	| 'exists'
	| 'create-org'
	| 'verify-org'
	| 'account'
	| 'dependency-setup'
	| 'locations'
	| 'it-setup'
	| 'mitchell-logins'
	| 'ops-logins'
	| 'supplier-network'
	| 'invite-users'
	| 'org-settings';

const SPLIT_LAYOUT_STEPS: CreateStep[] = ['exists', 'create-org', 'verify-org', 'account'];

/** Steps that belong to the post-account "Onboarding" phase */
const ONBOARDING_STEPS: CreateStep[] = ['dependency-setup', 'locations', 'mitchell-logins', 'ops-logins', 'it-setup', 'supplier-network', 'invite-users', 'org-settings'];

interface AccountDraft {
	selectedLocations: Array<string> | 'all';
	email: string;
	username: string;
	firstName: string;
	lastName: string;
	password: string;
	confirmPassword: string;
	position: Role | '';
	phoneNumber: string;
	otp: string;
	isOtpSent: boolean;
	isEmailVerified: boolean;
	termsAgreed: boolean;
}

const CreateAccount = () => {

	const [searchParams, setSearchParams] = useSearchParams();
	const queryClient = useQueryClient();

	const isITInvite = searchParams.get('type') === 'it';
	const itToken = searchParams.get('token') ?? '';

	const [currentStep, setCurrentStep] = useState<CreateStep>(
		searchParams.get('activation') ? 'verify-org' : isITInvite ? 'account' : 'exists'
	);
	const [organization, setOrganization] = useState<{
		_id: string;
		name: string;
		organizationMode?: 'full-time' | 'invoicing' | 'inventory';
	} | null>(null);
	const [locations, setLocations] = useState<Array<{ _id: string, title: string; }>>([]);

	useEffect(() => {
		if (!isITInvite || !itToken) return;
		(async () => {
			try {
				const API_URL = (await import('../constants/environment')).default;
				const { data } = await (await import('axios')).default.get(
					`${API_URL}/settings/account/validate-it-invite/${itToken}`
				);
				setOrganization({ _id: data.organizationId, name: data.organizationName });
				setAccountDraft(prev => ({
					...prev,
					position: 'it',
					email: data.email || prev.email,
					selectedLocations: 'all',
				}));
			} catch {
				// Invalid token — will fall through to normal flow
			}
		})();
	}, [isITInvite, itToken]);

	const isSetupPhase = SPLIT_LAYOUT_STEPS.includes(currentStep);
	const [orgExistsValue, setOrgExistsValue] = useState<'true' | 'false' | ''>('');
	const [createOrgName, setCreateOrgName] = useState('');
	const [createOrgAdminEmail, setCreateOrgAdminEmail] = useState('');
	const [createOrgType, setCreateOrgType] = useState<'' | 'sso' | 'mso' | 'franchise'>('');
	const [verifyOrgAccessCode, setVerifyOrgAccessCode] = useState('');
	const [accountDraft, setAccountDraft] = useState<AccountDraft>({
		selectedLocations: 'all',
		email: '',
		username: '',
		firstName: '',
		lastName: '',
		password: '',
		confirmPassword: '',
		position: '',
		phoneNumber: '',
		otp: '',
		isOtpSent: false,
		isEmailVerified: false,
		termsAgreed: false,
	});
	const [usesPt, setUsesPt] = useState(true);
	const [hasOpsCredentials, setHasOpsCredentials] = useState(false);
	const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

	// Pre-populate account email with the admin email entered during org creation
	useEffect(() => {
		if (currentStep === 'account' && createOrgAdminEmail && !accountDraft.email) {
			setAccountDraft(prev => ({ ...prev, email: createOrgAdminEmail }));
		}
	}, [currentStep, createOrgAdminEmail, accountDraft.email]);

	// Onboarding step persistence
	const sessionContext = useContext(SessionContext);
	const { data: onboardingData } = useGetOnboardingStep();
	const saveOnboardingStep = useSaveOnboardingStep();
	const toast = useToast();

	// Explicit "start over" from Login links (?fresh=1): clear saved step + wizard state before resume logic runs.
	useEffect(() => {
		const wantsFresh =
			searchParams.get('fresh') === '1' || searchParams.get('fresh')?.toLowerCase() === 'true';
		if (!wantsFresh) return;
		// Wait until validate-session settles so we have a stable session id before clearing server onboarding.
		if (sessionContext?.loading) return;

		let cancelled = false;
		(async () => {
			const sid = sessionContext?.sessionId;
			if (sid) {
				await axios
					.post(`${API_URL}/organization/onboarding-step`, { step: null }, { headers: { Authorization: sid } })
					.catch(() => {});
				await queryClient.refetchQueries({ queryKey: ['onboarding-step', sid] });
			}
			if (cancelled) return;

			setOrganization(null);
			setLocations([]);
			setOrgExistsValue('');
			setCreateOrgName('');
			setCreateOrgAdminEmail('');
			setCreateOrgType('');
			setVerifyOrgAccessCode('');
			setAccountDraft({
				selectedLocations: 'all',
				email: '',
				username: '',
				firstName: '',
				lastName: '',
				password: '',
				confirmPassword: '',
				position: '',
				phoneNumber: '',
				otp: '',
				isOtpSent: false,
				isEmailVerified: false,
				termsAgreed: false,
			});
			setUsesPt(true);

			const activation = searchParams.get('activation') === 'true';
			const itStart = searchParams.get('type') === 'it' && Boolean(searchParams.get('token'));
			setCurrentStep(activation ? 'verify-org' : itStart ? 'account' : 'exists');

			const nextParams = new URLSearchParams(searchParams);
			nextParams.delete('fresh');
			setSearchParams(nextParams, { replace: true });
		})();

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run when user opens /create-account?fresh=1
	}, [searchParams.get('fresh'), sessionContext?.sessionId, sessionContext?.loading]);

	// Restore saved onboarding when an authenticated user returns mid-flow (no ?fresh=1).
	useEffect(() => {
		const wantsFresh =
			searchParams.get('fresh') === '1' || searchParams.get('fresh')?.toLowerCase() === 'true';
		if (wantsFresh) return;
		if (!sessionContext?.sessionId) return;
		if (!onboardingData?.onboardingStep) return;

		const saved = onboardingData.onboardingStep as CreateStep;
		if (!ONBOARDING_STEPS.includes(saved)) return;
		// Only jump into post-account flow from the initial split-layout wizard entry.
		if (!SPLIT_LAYOUT_STEPS.includes(currentStep)) return;

		setOrganization({ _id: onboardingData.organizationId, name: onboardingData.organizationName });
		setLocations(onboardingData.locations);
		setCurrentStep(saved);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onboardingData, currentStep, sessionContext?.sessionId, searchParams]);

	// Save onboarding step to backend whenever it changes (only for post-account steps)
	useEffect(() => {
		if (!sessionContext?.sessionId) return;
		if (ONBOARDING_STEPS.includes(currentStep)) {
			saveOnboardingStep.mutateAsync(
				currentStep as 'dependency-setup' | 'locations' | 'it-setup' | 'mitchell-logins' | 'ops-logins' | 'supplier-network' | 'invite-users' | 'org-settings'
			).catch(() => {
				console.error('[onboarding] Failed to auto-save step:', currentStep);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, sessionContext?.sessionId]);

	const handleStepperStepClick = (step: CreateStep) => {
		// Only allow backtracking within post-account (onboarding) steps
		const POST_ACCOUNT_ORDER: CreateStep[] = ['dependency-setup', 'locations', 'mitchell-logins', 'ops-logins', 'it-setup', 'supplier-network', 'invite-users', 'org-settings'];
		const currentIdx = POST_ACCOUNT_ORDER.indexOf(currentStep);
		const targetIdx = POST_ACCOUNT_ORDER.indexOf(step);
		if (currentIdx >= 0 && targetIdx >= 0 && targetIdx < currentIdx) {
			setCurrentStep(step);
		}
	};

	const nextStepAfterItSetup: CreateStep = usesPt || hasOpsCredentials ? 'supplier-network' : 'invite-users';

	const renderStepContent = () => {
		switch (currentStep) {
			case 'exists':
				return (
					<OrgExistsStep
						setCurrentStep={setCurrentStep}
						orgExists={orgExistsValue}
						setOrgExists={setOrgExistsValue}
					/>
				);

			case 'create-org':
				return (
					<CreateOrganizationStep
						setCurrentStep={setCurrentStep}
						name={createOrgName}
						setName={setCreateOrgName}
						adminEmail={createOrgAdminEmail}
						setAdminEmail={setCreateOrgAdminEmail}
						orgType={createOrgType}
						setOrgType={setCreateOrgType}
					/>
				);
			case 'verify-org':
				return (
					<VerifyOrg
						setCurrentStep={setCurrentStep}
						setLocations={setLocations}
						setOrganization={setOrganization}
						accessCode={verifyOrgAccessCode}
						setAccessCode={setVerifyOrgAccessCode}
					/>
				);
			case 'account':
				return (
					<AccountStep
						locations={locations}
						organizationId={organization?._id ?? ''}
						organizationMode={organization?.organizationMode ?? 'full-time'}
						setCurrentStep={setCurrentStep}
						draft={accountDraft}
						setDraft={setAccountDraft}
						isITInvite={isITInvite}
					/>
				);
			case 'dependency-setup':
				return (
					<DependencySetupStep
						setCurrentStep={(step) => setCurrentStep(step)}
						usesPt={usesPt}
						onUsesPtChange={setUsesPt}
					/>
				);
			case 'locations':
				return (
					<LocationStep
						setCurrentStep={setCurrentStep}
						setLocations={setLocations}
						organizationId={organization?._id ?? ''}
						saveOnboardingStep={saveOnboardingStep}
						usesPt={usesPt}
					/>
				);
			case 'mitchell-logins':
			case 'ops-logins':
				return (
					<OptionalLoginsFlowStep
						setCurrentStep={setCurrentStep}
						organizationId={organization?._id ?? ''}
						locations={locations}
						onOpsCredentialsSaved={() => setHasOpsCredentials(true)}
					/>
				);
			case 'it-setup':
				return (
					<ITSetupStep
						setCurrentStep={(step: CreateStep) => setCurrentStep(step)}
						organizationId={organization?._id ?? ''}
						nextStepAfterItSetup={nextStepAfterItSetup}
					/>
				);
			case 'supplier-network':
				if (!usesPt && !hasOpsCredentials) {
					return (
						<InviteUsersFlowStep
							setCurrentStep={setCurrentStep}
							organizationId={organization?._id ?? ''}
							locations={locations}
							organizationMode={organization?.organizationMode ?? 'full-time'}
						/>
					);
				}
				return (
					<SupplierNetworkFlowStep
						setCurrentStep={setCurrentStep}
						organizationId={organization?._id ?? ''}
						usesPt={usesPt}
					/>
				);
			case 'invite-users':
				return (
					<InviteUsersFlowStep
						setCurrentStep={setCurrentStep}
						organizationId={organization?._id ?? ''}
						locations={locations}
						organizationMode={organization?.organizationMode ?? 'full-time'}
					/>
				);
			case 'org-settings':
				return (
					<OrgSettingsPresetFlowStep
						setCurrentStep={setCurrentStep}
						organizationId={organization?._id ?? ''}
					/>
				);
			default:
				return null;
		}
	};

	/* ─── Setup phase: header + borderless centered form + footer progress bar ─── */
	if (isSetupPhase) {
		return (
			<>
				<Flex
					direction="column"
					h="100vh"
					w="100%"
					bg="white"
					position="relative"
				>
					{/* Header */}
					<Flex
						as="header"
						w="full"
						align="center"
						justify="space-between"
						px={{ base: 4, md: 6 }}
						py={3}
						borderBottomWidth="1px"
						borderBottomColor="gray.200"
						bg="white"
						flexShrink={0}
						gap={4}
					>
						<Flex align="center" flex={1} minW={0}>
							<Box as={RouterLink} to="/" display="flex" alignItems="center">
								<Image src={YaxleLogo} alt="Yaxle" h={7} />
							</Box>
						</Flex>

						<Text
							fontSize="sm"
							fontWeight="semibold"
							color="gray.800"
							textAlign="center"
							flex={1}
							noOfLines={1}
						>
							{getSetupStepLabel(currentStep)}
						</Text>

						<HStack spacing={2} flex={1} justify="flex-end">
							<HelpPopover />
						</HStack>
					</Flex>

					{/* Body — borderless, vertically centered form */}
					<Flex
						flex={1}
						minH={0}
						w="full"
						justify="center"
						overflow="hidden"
					>
						<Flex
							direction="column"
							w="full"
							maxW="lg"
							px={{ base: 4, md: 8 }}
							py={6}
							flex={1}
							minH={0}
							overflowY="auto"
						>
							<Box w="full" my="auto">
								{renderStepContent()}
								<Text mt={6} textAlign="center" fontSize="sm" color="gray.600">
									Already have an account?{' '}
									<RouterLink to="/login" style={{ color: '#0071E3', textDecoration: 'underline' }}>
										Login here
									</RouterLink>
								</Text>
							</Box>
						</Flex>
					</Flex>

					{/* Footer — slim 3-segment progress bar + Privacy below */}
					<Box
						w="full"
						px={{ base: 4, md: 6 }}
						pb={4}
						pt={2}
						flexShrink={0}
						bg="white"
					>
						<SetupProgressBar currentStep={currentStep} />
						{currentStep !== 'account' && (
							<HStack spacing={3} justify="center" pt={3} fontSize="xs" color="gray.500">
								<Link
									onClick={() => setIsPrivacyModalOpen(true)}
									_hover={{ textDecoration: 'underline' }}
									cursor="pointer"
								>
									Privacy policy
								</Link>
							</HStack>
						)}
					</Box>
				</Flex>
				<PrivacyPolicy isOpen={isPrivacyModalOpen} onClose={() => setIsPrivacyModalOpen(false)} />
			</>
		);
	}

	const handleSaveProgress = () => {
		if (!ONBOARDING_STEPS.includes(currentStep)) return;
		saveOnboardingStep.mutate(
			currentStep as 'dependency-setup' | 'locations' | 'it-setup' | 'mitchell-logins' | 'ops-logins' | 'supplier-network' | 'invite-users' | 'org-settings',
			{
				onSuccess: () => {
					toast({
						title: 'Progress saved',
						status: 'success',
						duration: 2000,
						isClosable: true,
					});
				},
				onError: () => {
					toast({
						title: 'Failed to save',
						description: 'Could not save your progress. Please try again.',
						status: 'error',
						duration: 3000,
						isClosable: true,
					});
				},
			}
		);
	};

	/* ─── Onboarding phase: header + borderless content + footer progress bar ─── */
	return (
		<>
			<Flex
				direction="column"
				h="100vh"
				w="100%"
				bg="white"
				position="relative"
			>
				{/* Header */}
				<Flex
					as="header"
					w="full"
					align="center"
					justify="space-between"
					px={{ base: 4, md: 6 }}
					py={3}
					borderBottomWidth="1px"
					borderBottomColor="gray.200"
					bg="white"
					flexShrink={0}
					gap={4}
				>
					<Flex align="center" flex={1} minW={0}>
						<Box as={RouterLink} to="/" display="flex" alignItems="center">
							<Image src={YaxleLogo} alt="Yaxle" h={7} />
						</Box>
					</Flex>

					<Text
						fontSize="sm"
						fontWeight="semibold"
						color="gray.800"
						textAlign="center"
						flex={1}
						noOfLines={1}
					>
						{getOnboardingStepLabel(currentStep)}
					</Text>

					<HStack spacing={2} flex={1} justify="flex-end">
						<HelpPopover />
						<Button
							size="sm"
							variant="outline"
							onClick={handleSaveProgress}
							isLoading={saveOnboardingStep.isPending}
							loadingText="Saving"
						>
							Save
						</Button>
					</HStack>
				</Flex>

				{/* Body — centered, max-width content area (borderless) */}
				<Flex
					flex={1}
					minH={0}
					w="full"
					justify="center"
					overflow="hidden"
				>
					<Flex
						direction="column"
						w="full"
						maxW="1200px"
						px={{ base: 4, md: 8 }}
						py={6}
						flex={1}
						minH={0}
					>
						{renderStepContent()}
					</Flex>
				</Flex>

				{/* Step action footer (portal target) — full viewport width so
				    Back sits at the far LHS and Skip/Primary at the far RHS,
				    regardless of the content's max-width. */}
				<Box
					id={STEP_FOOTER_PORTAL_ID}
					w="full"
					px={{ base: 4, md: 6 }}
					py={3}
					minH="60px"
					flexShrink={0}
					bg="white"
				/>

				{/* Footer — slim segmented progress bar with hover labels + Privacy below */}
				<Box
					w="full"
					px={{ base: 4, md: 6 }}
					pb={4}
					pt={2}
					flexShrink={0}
					bg="white"
				>
					<SetupStepper
						currentStep={currentStep}
						onStepClick={handleStepperStepClick}
					/>
					<HStack spacing={3} justify="center" pt={3} fontSize="xs" color="gray.500">
						<Link
							onClick={() => setIsPrivacyModalOpen(true)}
							_hover={{ textDecoration: 'underline' }}
							cursor="pointer"
						>
							Privacy policy
						</Link>
					</HStack>
				</Box>
			</Flex>
			<PrivacyPolicy isOpen={isPrivacyModalOpen} onClose={() => setIsPrivacyModalOpen(false)} />
		</>
	);
};

/* ─── Help popover (header RHS) ─── */

const SUPPORT_EMAIL = 'support@yaxle.com';

const HelpPopover = () => {
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(SUPPORT_EMAIL);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1500);
		} catch {
			// Clipboard unavailable — silent fail; user can copy manually
		}
	};

	return (
		<Popover placement="bottom-end" gutter={8}>
			<PopoverTrigger>
				<Button
					size="sm"
					variant="ghost"
					color="gray.700"
					leftIcon={<Icon as={FaQuestionCircle as React.ElementType} boxSize={3.5} />}
					_hover={{ bg: 'gray.100' }}
				>
					Help
				</Button>
			</PopoverTrigger>
			<PopoverContent w="auto" maxW="320px">
				<PopoverArrow />
				<PopoverBody p={3}>
					<VStack align="stretch" spacing={2}>
						<Text fontSize="xs" color="gray.500">
							Need a hand? Reach our team at:
						</Text>
						<HStack
							spacing={2}
							borderWidth={1}
							borderColor="gray.200"
							borderRadius="md"
							px={2.5}
							py={1.5}
							bg="gray.50"
							justify="space-between"
						>
							<Text fontSize="sm" fontWeight="medium" color="gray.800">
								{SUPPORT_EMAIL}
							</Text>
							<IconButton
								aria-label={copied ? 'Copied' : 'Copy support email'}
								icon={
									<Icon
										as={(copied ? FaCheck : FaCopy) as React.ElementType}
										boxSize={3}
										color={copied ? 'green.500' : 'gray.500'}
									/>
								}
								size="xs"
								variant="ghost"
								onClick={handleCopy}
							/>
						</HStack>
					</VStack>
				</PopoverBody>
			</PopoverContent>
		</Popover>
	);
};

interface OrgExistsInterface {
	setCurrentStep: React.Dispatch<React.SetStateAction<CreateStep>>;
	orgExists: 'true' | 'false' | '';
	setOrgExists: React.Dispatch<React.SetStateAction<'true' | 'false' | ''>>;
}
const OrgExistsStep = ({ setCurrentStep, orgExists, setOrgExists }: OrgExistsInterface) => {
	const navigate = useNavigate();

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (orgExists === 'true') {
			setCurrentStep('verify-org');
		} else {
			setCurrentStep('create-org');
		}
	};

	return (
		<VStack spacing={4} as="form" onSubmit={handleSubmit} align="start" width="100%">
			<VStack spacing={6} align="center" width="100%">
				<Heading size="md" textAlign="center" width="100%">
					Does your organization already have an account with us?
				</Heading>
				<FormControl isRequired width="100%">
					<RadioGroup value={orgExists} onChange={(value: 'true' | 'false') => setOrgExists(value)}>
						<HStack spacing={4} align="stretch">
							<Box
								as="button"
								type="button"
								onClick={() => setOrgExists('true')}
								borderWidth="1px"
								borderColor={orgExists === 'true' ? 'green.300' : 'gray.200'}
								bg={orgExists === 'true' ? 'green.50' : 'white'}
								borderRadius="lg"
								p={4}
								flex={1}
								transition="all 0.2s ease"
								_hover={{ borderColor: 'green.300', bg: 'green.50' }}
								textAlign="center"
							>
								<VStack spacing={2}>
									<Flex w="34px" h="34px" borderRadius="md" bg={orgExists === 'true' ? 'green.500' : 'gray.100'} align="center" justify="center" mx="auto">
										<Icon as={FaCheckCircle as React.ElementType} color={orgExists === 'true' ? 'white' : 'gray.500'} boxSize={4} />
									</Flex>
									<Box>
										<Text fontSize="sm" fontWeight="semibold" color="gray.800">Yes</Text>
										<Text fontSize="xs" color="gray.500">I already have an organization account</Text>
									</Box>
								</VStack>
								<Radio value="true" display="none">Yes</Radio>
							</Box>

							<Box
								as="button"
								type="button"
								onClick={() => setOrgExists('false')}
								borderWidth="1px"
								borderColor={orgExists === 'false' ? 'red.300' : 'gray.200'}
								bg={orgExists === 'false' ? 'red.50' : 'white'}
								borderRadius="lg"
								p={4}
								flex={1}
								transition="all 0.2s ease"
								_hover={{ borderColor: 'red.300', bg: 'red.50' }}
								textAlign="center"
							>
								<VStack spacing={2}>
									<Flex w="34px" h="34px" borderRadius="md" bg={orgExists === 'false' ? 'red.500' : 'gray.100'} align="center" justify="center" mx="auto">
										<Icon as={FaPlusCircle as React.ElementType} color={orgExists === 'false' ? 'white' : 'gray.500'} boxSize={4} />
									</Flex>
									<Box>
										<Text fontSize="sm" fontWeight="semibold" color="gray.800">No</Text>
										<Text fontSize="xs" color="gray.500">I need to create a new organization account</Text>
									</Box>
								</VStack>
								<Radio value="false" display="none">No</Radio>
							</Box>
						</HStack>
					</RadioGroup>
				</FormControl>
			</VStack>

			<HStack spacing={4} width="100%" pt={4}>
				<Button
					variant="outline"
					onClick={() => navigate('/login')}
					width="50%"
					size="sm"
				>
					Back
				</Button>
				<Button
					bg="#0071E3"
					color="white"
					_hover={{ bg: '#3141a5' }}
					width="50%"
					type="submit"
					size="sm"
					disabled={orgExists === ''}
				>
					Next
				</Button>
			</HStack>
		</VStack>
	);
};


interface CreateOrganizationInterface {
	setCurrentStep: React.Dispatch<React.SetStateAction<CreateStep>>;
	name: string;
	setName: React.Dispatch<React.SetStateAction<string>>;
	adminEmail: string;
	setAdminEmail: React.Dispatch<React.SetStateAction<string>>;
	orgType: '' | 'sso' | 'mso' | 'franchise';
	setOrgType: React.Dispatch<React.SetStateAction<'' | 'sso' | 'mso' | 'franchise'>>;
}
const CreateOrganizationStep = ({
	setCurrentStep,
	name,
	setName,
	adminEmail,
	setAdminEmail,
	orgType,
	setOrgType,
}: CreateOrganizationInterface) => {

	const toast = useToast();
	const createOrganization = useCreateOrganization();
	const [organizationMode, setOrganizationMode] = useState<'full-time' | 'invoicing' | 'inventory'>('full-time');

	const handleBack = () => {
		setCurrentStep('exists');
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (orgType === '') {
			return;
		}
		createOrganization.mutateAsync({
			orgType,
			organizationMode,
			adminEmail,
			name
		}).then(() => {
			toast({
				title: 'Organization Created',
				description: 'We are reviewing your organization, we will reach out to the email provided with your access code when your registration is approved!',
				status: 'success',
				duration: 10000,
				isClosable: true,
			});
			setCurrentStep('verify-org');
		}).catch(() => {
			toast({
				title: 'Error',
				description: 'Oops! Something went wrong. Please try again',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
		});
	};

	return (
		<VStack spacing={4} as="form" onSubmit={handleSubmit} align="start" width="100%">
			<VStack spacing={6} align="start" width="100%">
				<FormControl isRequired width="100%">
					<FormLabel>Organization Name</FormLabel>
					<Input
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="Main Street Auto Body"
					/>
				</FormControl>
				<FormControl isRequired width="100%">
					<FormLabel>Admin Email</FormLabel>
					<Input
						value={adminEmail}
						onChange={(e) => setAdminEmail(e.target.value)}
						placeholder="john@mainstreetautobody.com"
					/>
				</FormControl>
				<FormControl isRequired width="100%">
					<FormLabel>Organization Type</FormLabel>
					<VStack spacing={3} align="stretch">
						{([
							{ value: 'sso' as const, label: 'Single Store', description: 'One location, one team', icon: FaStore, color: 'teal' },
							{ value: 'mso' as const, label: 'MSO', description: 'Multiple locations under one organization', icon: FaBuilding, color: 'purple' },
							{ value: 'franchise' as const, label: 'Franchise', description: 'Independently owned locations, shared brand', icon: FaSitemap, color: 'orange' },
						]).map((option) => (
							<Box
								key={option.value}
								as="button"
								type="button"
								onClick={() => setOrgType(option.value)}
								borderWidth="2px"
								borderColor={orgType === option.value ? `${option.color}.400` : 'gray.200'}
								bg={orgType === option.value ? `${option.color}.50` : 'white'}
								borderRadius="lg"
								p={4}
								transition="all 0.2s ease"
								_hover={{
									borderColor: `${option.color}.300`,
									bg: orgType === option.value ? `${option.color}.50` : 'gray.50',
									transform: 'translateY(-1px)',
									shadow: 'none',
								}}
								textAlign="left"
							>
								<HStack spacing={3} align="center" justify="space-between">
									<HStack spacing={3} align="center">
										<Flex
											w="40px"
											h="40px"
											borderRadius="lg"
											bg={orgType === option.value ? `${option.color}.500` : `${option.color}.100`}
											align="center"
											justify="center"
											flexShrink={0}
										>
											<Icon
												as={option.icon as React.ElementType}
												color={orgType === option.value ? 'white' : `${option.color}.500`}
												boxSize={5}
											/>
										</Flex>
										<Box>
											<Text fontSize="sm" fontWeight="semibold" color="gray.800">
												{option.label}
											</Text>
											<Text fontSize="xs" color="gray.500">
												{option.description}
											</Text>
										</Box>
									</HStack>
									{orgType === option.value && (
										<Icon as={FaCheckCircle as React.ElementType} color={`${option.color}.500`} boxSize={4} flexShrink={0} />
									)}
								</HStack>
							</Box>
						))}
					</VStack>
				</FormControl>
				<FormControl isRequired width="100%">
					<FormLabel>Organization Mode</FormLabel>
					<RadioGroup value={organizationMode} onChange={(value: 'full-time' | 'invoicing' | 'inventory') => setOrganizationMode(value)}>
						<Stack direction="column" spacing={4}>
							<Radio value="full-time" size="lg">Full-Time</Radio>
							<Radio value="invoicing" size="lg">Invoicing</Radio>
							<Radio value="inventory" size="lg">Inventory</Radio>
						</Stack>
					</RadioGroup>
				</FormControl>
			</VStack>

			<HStack spacing={4} width="100%" pt={4}>
				<Button
					variant="outline"
					onClick={handleBack}
					width="50%"
					size="sm"
				>
					Back
				</Button>
				<Button
					bg="#0071E3"
					color="white"
					_hover={{ bg: '#3141a5' }}
					width="50%"
					type="submit"
					size="sm"
					isLoading={createOrganization.isPending}
					loadingText="Creating..."
					isDisabled={orgType.length === 0 || adminEmail.length === 0 || name.length === 0}
				>
					Create Organization
				</Button>
			</HStack>
		</VStack>
	);
};


interface VerifyOrgInterface {
	setCurrentStep: React.Dispatch<React.SetStateAction<CreateStep>>;
	setOrganization: React.Dispatch<React.SetStateAction<{
		_id: string;
		name: string;
		organizationMode?: 'full-time' | 'invoicing' | 'inventory';
	} | null>>;
	setLocations: React.Dispatch<React.SetStateAction<{
		_id: string;
		title: string;
	}[]>>;
	accessCode: string;
	setAccessCode: React.Dispatch<React.SetStateAction<string>>;
}
const VerifyOrg = ({
	setOrganization,
	setCurrentStep,
	setLocations,
	accessCode,
	setAccessCode,
}: VerifyOrgInterface) => {

	const toast = useToast();
	const findOrganization = useFindOrganization();

	const handleBack = () => {
		setCurrentStep('exists');
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		findOrganization.mutateAsync(accessCode)
			.then((res) => {
				setOrganization(res.organization);
				setLocations(res.locations);
				setCurrentStep('account');
			})
			.catch((err: unknown) => {
				if (err instanceof AxiosError && err.status === 401) {
					toast({
						title: 'Error',
						description: 'Your organization is still pending approval',
						status: 'error',
						duration: 3000,
						isClosable: true,
					});
				} else {
					toast({
						title: 'Error',
						description: 'Could not find your organization. Please try again!',
						status: 'error',
						duration: 3000,
						isClosable: true,
					});
				}
			});
	};

	return (
		<VStack spacing={4} as="form" onSubmit={handleSubmit} align="start" width="100%">
			<VStack spacing={6} align="start" width="100%">
				<Heading size="md" textAlign="center" width="100%">
					Organization Access Code
				</Heading>
				<FormControl isRequired width="100%">
					<FormLabel>Access Code</FormLabel>
					<Input
						value={accessCode}
						onChange={(e) => setAccessCode(e.target.value)}
						placeholder="partbay-...."
					/>
				</FormControl>
				<Text textAlign="left" color="gray.600" width="100%">
					Your organization administrator can view the organization access code in their settings.
				</Text>
			</VStack>

			<HStack spacing={4} width="100%" pt={4}>
				<Button
					variant="outline"
					onClick={handleBack}
					width="50%"
					size="sm"
				>
					Back
				</Button>
				<Button
					bg="#0071E3"
					color="white"
					_hover={{ bg: '#3141a5' }}
					width="50%"
					type="submit"
					size="sm"
					isLoading={findOrganization.isPending}
					loadingText="Verifying..."
					isDisabled={accessCode.length === 0}
				>
					Verify Organization
				</Button>
			</HStack>
		</VStack>
	);
};


interface LocationStepInterface {
	setCurrentStep: React.Dispatch<React.SetStateAction<CreateStep>>;
	organizationId: string;
	setLocations: React.Dispatch<React.SetStateAction<{
		_id: string;
		title: string;
	}[]>>;
	saveOnboardingStep: ReturnType<typeof useSaveOnboardingStep>;
	usesPt?: boolean;
}
const LocationStep = ({
	setCurrentStep,
	organizationId,
	setLocations,
	saveOnboardingStep,
	usesPt = true,
}: LocationStepInterface) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const pullPt = usePullPartsTraderLocations();
	const pullCcc = usePullCccLocations();
	const reconcile = useReconcileLocations();
	const createTesting = useCreateTestingLocations();
	const [searchParams] = useSearchParams();
	const canLoadTestCredentials = searchParams.get('test') === '1';

	const [subStep, setSubStep] = useState<'credentials' | 'mapping'>('credentials');
	const [ptLocations, setPtLocations] = useState<PartstraderLocation[]>([]);
	const [cccLocations, setCccLocations] = useState<CccLocation[]>([]);
	const [suggestedMappings, setSuggestedMappings] = useState<LocationMapping[]>([]);
	const [cccMappings, setCccMappings] = useState<Record<string, CccRowMapping>>({});
	const [isPulling, setIsPulling] = useState(false);

	const hasReconciled = useRef(false);

	const ptLocationsReady = usesPt ? pullPt.locationsReady : true;

	useEffect(() => {
		if (!ptLocationsReady || !pullCcc.isComplete) return;
		if (hasReconciled.current) return;
		hasReconciled.current = true;

		const ptLocs = usesPt ? (pullPt.locations ?? []) : [];
		const cccLocs = pullCcc.locations ?? [];
		setPtLocations(ptLocs);
		setCccLocations(cccLocs);
		setIsPulling(false);

		if (ptLocs.length === 0 && cccLocs.length === 0) {
			toast({
				title: 'No locations found',
				description: 'No locations were returned. Please verify your credentials and try again.',
				status: 'warning',
				duration: 5000,
				isClosable: true,
			});
			return;
		}

		const descParts = [];
		if (usesPt) descParts.push(`PartsTrader: ${ptLocs.length} locations.`);
		descParts.push(`CCC: ${cccLocs.length} locations.`);
		toast({
			title: 'Locations pulled',
			description: descParts.join(' '),
			status: 'success',
			duration: 5000,
			isClosable: true,
		});

		(async () => {
			let reconciledMappings: LocationMapping[] = [];
			if (usesPt && ptLocs.length > 0) {
				try {
					const reconciled = await reconcile.mutateAsync({
						partstraderLocations: ptLocs,
						cccLocations: cccLocs,
					});
					reconciledMappings = reconciled.mappings;
					setSuggestedMappings(reconciled.mappings);
				} catch {
					// Reconcile failed
				}
			}
			const initial: Record<string, CccRowMapping> = {};
			cccLocs.forEach((ccc) => { initial[ccc.id] = { enabled: true, ptIds: [] }; });
			reconciledMappings.forEach((m) => {
				if (initial[m.cccId]) {
					initial[m.cccId].ptIds.push(m.partstraderId);
				}
			});
			setCccMappings(initial);
			setSubStep('mapping');
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ptLocationsReady, pullCcc.isComplete, pullPt.locations, pullCcc.locations]);

	useEffect(() => {
		if ((usesPt && pullPt.isFailed) || pullCcc.isFailed) {
			setIsPulling(false);
			toast({
				title: 'Pull failed',
				description: (usesPt ? pullPt.error : undefined) || pullCcc.error || 'Could not pull locations. Please check credentials.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		}
	}, [usesPt, pullPt.isFailed, pullCcc.isFailed, pullPt.error, pullCcc.error, toast]);

	const handlePull = async (values: { partsTraderUsername: string; partsTraderPassword: string; cccUsername: string; cccPassword: string; }) => {
		hasReconciled.current = false;
		setIsPulling(true);
		if (usesPt) pullPt.reset();
		pullCcc.reset();
		queryClient.removeQueries({ queryKey: ['pull-ccc-status', organizationId] });
		if (usesPt) queryClient.removeQueries({ queryKey: ['pull-pt-status', organizationId] });
		toast.closeAll();
		try {
			const pulls: Promise<unknown>[] = [
				pullCcc.submit.mutateAsync({ organizationId, username: values.cccUsername, password: values.cccPassword }),
			];
			if (usesPt) {
				pulls.push(
					pullPt.submit.mutateAsync({ organizationId, username: values.partsTraderUsername, password: values.partsTraderPassword }),
				);
			}
			await Promise.all(pulls);
		} catch (err: unknown) {
			setIsPulling(false);
			toast({
				title: 'Pull failed',
				description: err instanceof Error ? err.message : 'Could not submit pull request.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		}
	};

	const handleLoadTestCredentials = async () => {
		toast({
			title: 'Test credentials',
			description: 'Test credentials endpoint not yet implemented.',
			status: 'info',
			duration: 3000,
			isClosable: true,
		});
	};

	const handleToggleCcc = (cccId: string) => {
		setCccMappings((prev) => ({
			...prev,
			[cccId]: { ...prev[cccId], enabled: !prev[cccId]?.enabled },
		}));
	};

	const handleAddPt = (cccId: string, ptId: string) => {
		setCccMappings((prev) => ({
			...prev,
			[cccId]: { ...prev[cccId], ptIds: [...(prev[cccId]?.ptIds ?? []), ptId] },
		}));
	};

	const handleRemovePt = (cccId: string, ptId: string) => {
		setCccMappings((prev) => ({
			...prev,
			[cccId]: { ...prev[cccId], ptIds: (prev[cccId]?.ptIds ?? []).filter((id) => id !== ptId) },
		}));
	};

	const handleConfirmMapping = async (confirmedMappings: LocationMapping[]) => {
		if (confirmedMappings.length === 0) {
			toast({
				title: 'No locations selected',
				description: 'Please select at least one location.',
				status: 'warning',
				duration: 3000,
				isClosable: true,
			});
			return;
		}
		try {
			const cccMap = new Map(cccLocations.map((c) => [c.id, c]));
			const res = await createTesting.mutateAsync({
				organizationId,
				mappings: confirmedMappings.map((m) => {
					const ccc = cccMap.get(m.cccId);
					return {
						partstraderId: m.partstraderId,
						cccId: m.cccId,
						title: ccc?.name,
						address: ccc?.address,
						city: ccc?.city,
						state: ccc?.state,
						zip: ccc?.zip,
					};
				}),
			});
			setLocations(res.locations);
			// Explicitly save the onboarding step before advancing so it persists on refresh
			await saveOnboardingStep.mutateAsync('mitchell-logins');
			toast({
				title: 'Locations saved',
				description: `${res.locations.length} locations created.`,
				status: 'success',
				duration: 3000,
				isClosable: true,
			});
			setCurrentStep('mitchell-logins');
		} catch (err: unknown) {
			toast({
				title: 'Failed to save',
				description: err instanceof Error ? err.message : 'Could not create locations.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		}
	};

	const isLoading = isPulling || (usesPt && pullPt.isPolling) || pullCcc.isPolling || reconcile.isPending || createTesting.isPending;

	const getStatusMessage = (): string | undefined => {
		if (usesPt && pullPt.isPolling && pullCcc.isPolling) return 'Pulling locations from PartsTrader and CCC ONE...';
		if (usesPt && pullPt.isPolling) return 'Pulling locations from PartsTrader...';
		if (pullCcc.isPolling) return 'Pulling locations from CCC ONE...';
		if (reconcile.isPending) return 'Matching locations...';
		if (createTesting.isPending) return 'Creating locations...';
		return undefined;
	};

	if (subStep === 'mapping') {
		return (
			<LocationMappingUI
				partstraderLocations={ptLocations}
				cccLocations={cccLocations}
				suggestedMappings={suggestedMappings}
				cccMappings={cccMappings}
				onConfirm={handleConfirmMapping}
				isConfirmLoading={createTesting.isPending}
				onToggleCcc={handleToggleCcc}
				onAddPt={handleAddPt}
				onRemovePt={handleRemovePt}
				onBack={() => setSubStep('credentials')}
				onBackToSetup={() => setCurrentStep('dependency-setup')}
				usesPt={usesPt}
			/>
		);
	}

	return (
		<CredentialsForm
			onPull={handlePull}
			isLoading={isLoading}
			statusMessage={getStatusMessage()}
			onLoadTestCredentials={handleLoadTestCredentials}
			canLoadTestCredentials={canLoadTestCredentials}
			onBack={() => setCurrentStep('dependency-setup')}
			usesPt={usesPt}
		/>
	);
};

/* ─── Combined Optional Logins (Mitchell + OPS in one tabbed view) ─── */

interface OptionalLoginsFlowStepProps {
	setCurrentStep: React.Dispatch<React.SetStateAction<CreateStep>>;
	organizationId: string;
	locations: Array<{ _id: string; title: string; }>;
	onOpsCredentialsSaved: () => void;
}

const OptionalLoginsFlowStep = ({
	setCurrentStep,
	organizationId,
	locations,
	onOpsCredentialsSaved,
}: OptionalLoginsFlowStepProps) => {
	type LoginTab = 'mitchell' | 'tesla' | 'ops';
	const [activeTab, setActiveTab] = useState<LoginTab>('mitchell');
	const uploadModal = useDisclosure();

	const steps: { key: LoginTab; label: string; }[] = [
		{ key: 'mitchell', label: 'Mitchell' },
		{ key: 'tesla', label: 'Tesla' },
		{ key: 'ops', label: 'OPS' },
	];
	const completedBefore = (key: LoginTab): boolean => {
		const order: LoginTab[] = ['mitchell', 'tesla', 'ops'];
		return order.indexOf(key) < order.indexOf(activeTab);
	};

	return (
		<Flex direction="column" width="100%" flex={1} minH={0}>
			<Flex justify="space-between" align="center" mb={4} flexShrink={0}>
				<Heading size="md">Platform Logins</Heading>
				<Button size="sm" variant="outline" onClick={uploadModal.onOpen}>
					Upload Template
				</Button>
			</Flex>

			<HStack spacing={3} width="100%" mb={4} flexShrink={0}>
				{steps.map((step, idx) => {
					const isActive = activeTab === step.key;
					const isCompleted = completedBefore(step.key);
					return (
						<Box
							key={step.key}
							flex={1}
							cursor="pointer"
							onClick={() => setActiveTab(step.key)}
						>
							<Text
								fontSize="sm"
								fontWeight="medium"
								color={isActive ? '#0071E3' : isCompleted ? '#0071E3' : 'gray.400'}
								mb={1.5}
							>
								{idx + 1}. {step.label}
							</Text>
							<Box
								h="4px"
								borderRadius="full"
								bg={isActive || isCompleted ? '#0071E3' : 'gray.200'}
								transition="background 0.2s"
							/>
						</Box>
					);
				})}
			</HStack>

			{activeTab === 'mitchell' && (
				<MitchellLoginsStep
					organizationId={organizationId}
					locations={locations}
					onBack={() => setCurrentStep('locations')}
					onSkip={() => setActiveTab('tesla')}
					onNext={() => setActiveTab('tesla')}
					isUploadOpen={uploadModal.isOpen}
					onCloseUpload={uploadModal.onClose}
				/>
			)}

			{activeTab === 'tesla' && (
				<TeslaLoginsStep
					onBack={() => setActiveTab('mitchell')}
					onSkip={() => setActiveTab('ops')}
					onNext={() => setActiveTab('ops')}
				/>
			)}

			{activeTab === 'ops' && (
				<OpsTraxLoginsStep
					organizationId={organizationId}
					locations={locations}
					onBack={() => setActiveTab('tesla')}
					onSkip={() => setCurrentStep('it-setup')}
					onComplete={() => {
						onOpsCredentialsSaved();
						setCurrentStep('it-setup');
					}}
					isUploadOpen={uploadModal.isOpen}
					onCloseUpload={uploadModal.onClose}
				/>
			)}
		</Flex>
	);
};

/* ─── Flow step wrappers ─── */

interface SupplierNetworkFlowStepProps {
	setCurrentStep: React.Dispatch<React.SetStateAction<CreateStep>>;
	organizationId: string;
	usesPt?: boolean;
}

const SupplierNetworkFlowStep = ({
	setCurrentStep,
	organizationId,
	usesPt = true,
}: SupplierNetworkFlowStepProps) => {
	return (
		<SupplierNetworkStep
			organizationId={organizationId}
			onComplete={() => setCurrentStep('invite-users')}
			onSkip={() => setCurrentStep('invite-users')}
			onBack={() => setCurrentStep('it-setup')}
			usesPt={usesPt}
		/>
	);
};

interface InviteUsersFlowStepProps {
	setCurrentStep: React.Dispatch<React.SetStateAction<CreateStep>>;
	organizationId: string;
	locations: { _id: string; title: string; }[];
	organizationMode?: 'full-time' | 'invoicing' | 'inventory';
}

const InviteUsersFlowStep = ({
	setCurrentStep,
	organizationId,
	locations,
	organizationMode = 'full-time',
}: InviteUsersFlowStepProps) => {
	return (
		<InviteUsersStep
			organizationId={organizationId}
			locations={locations}
			onComplete={() => setCurrentStep('org-settings')}
			onSkip={() => setCurrentStep('org-settings')}
			onBack={() => setCurrentStep('supplier-network')}
			isInvoicingOrg={organizationMode === 'invoicing'}
		/>
	);
};

interface OrgSettingsPresetFlowStepProps {
	setCurrentStep: React.Dispatch<React.SetStateAction<CreateStep>>;
	organizationId: string;
}

const OrgSettingsPresetFlowStep = ({
	setCurrentStep,
	organizationId,
}: OrgSettingsPresetFlowStepProps) => {
	const navigate = useNavigate();
	const saveOnboardingStep = useSaveOnboardingStep();
	return (
		<OrgSettingsPresetStep
			organizationId={organizationId}
			onComplete={() => {
				saveOnboardingStep.mutate(null);
				navigate('/settings');
			}}
			onBack={() => setCurrentStep('invite-users')}
		/>
	);
};

interface AccountStepInterface {
	locations: {
		_id: string;
		title: string;
	}[];
	organizationId: string;
	organizationMode: 'full-time' | 'invoicing' | 'inventory';
	setCurrentStep: React.Dispatch<React.SetStateAction<CreateStep>>;
	draft: AccountDraft;
	setDraft: React.Dispatch<React.SetStateAction<AccountDraft>>;
	isITInvite?: boolean;
}

const formatPhoneNumber = (value: string): string => {
	// Strip the "+1" prefix we add so it doesn't get double-counted
	const stripped = value.replace(/^\+1\s*/, '');
	const digits = stripped.replace(/\D/g, '').slice(0, 10);
	if (digits.length === 0) return '';
	if (digits.length <= 3) return `+1 (${digits}`;
	if (digits.length <= 6) return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
	return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const AccountStep = ({
	locations,
	organizationId,
	organizationMode,
	setCurrentStep,
	draft,
	setDraft,
	isITInvite = false,
}: AccountStepInterface) => {
	const toast = useToast();
	const navigate = useNavigate();

	const submitAccountCreation = useSubmitAccountCreation();
	const sendOTP = useSendOTP();
	const verifyOTP = useVerifyOTP();
	const sessionContext = useContext(SessionContext);

	const finishSignupAndRouteAfterLogin = (sessionId: string) => {
		sessionContext?.login(sessionId);
		if (isITInvite || organizationMode === 'invoicing' || organizationMode === 'inventory') {
			void axios
				.post(`${API_URL}/organization/onboarding-step`, { step: null }, { headers: { Authorization: sessionId } })
				.finally(() => navigate('/settings'));
		} else {
			setCurrentStep('dependency-setup');
		}
	};

	const {
		selectedLocations,
		email,
		username,
		firstName,
		lastName,
		password,
		position,
		phoneNumber,
		confirmPassword,
		otp,
		isOtpSent,
		isEmailVerified,
		termsAgreed,
	} = draft;

	const isInvoicingOrg = organizationMode === 'invoicing';

	const updateDraft = <K extends keyof AccountDraft>(key: K, value: AccountDraft[K]) => {
		setDraft((prev) => ({ ...prev, [key]: value }));
	};

	useEffect(() => {
		if (selectedLocations === 'all') return;
		const validIds = new Set(locations.map((l) => l._id));
		const filtered = selectedLocations.filter((id) => validIds.has(id));
		if (filtered.length !== selectedLocations.length) {
			updateDraft('selectedLocations', filtered.length === 0 ? 'all' : filtered);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locations]);

	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const [locationPopoverOpen, setLocationPopoverOpen] = useState(false);
	const [locationSearch, setLocationSearch] = useState('');
	const locationTriggerRef = useRef<HTMLButtonElement>(null);
	const [locationPopoverWidth, setLocationPopoverWidth] = useState<number | string>('100%');

	useEffect(() => {
		if (locationPopoverOpen && locationTriggerRef.current) {
			setLocationPopoverWidth(Math.max(locationTriggerRef.current.offsetWidth, 260));
		}
	}, [locationPopoverOpen]);

	useEffect(() => {
		if (!locationPopoverOpen) setLocationSearch('');
	}, [locationPopoverOpen]);

	const handleAllLocationsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.checked) {
			updateDraft('selectedLocations', 'all');
		}
	};

	const handleIndividualLocationsChange = (selectedValues: (string | number)[]) => {
		const newValue = selectedValues as string[];
		const finalValue: Array<string> | 'all' = newValue.length === 0 ? 'all' : newValue;
		updateDraft('selectedLocations', finalValue);
	};

	const handleRemoveLocation = (locationIdToRemove: string) => {
		if (selectedLocations === 'all' || !Array.isArray(selectedLocations)) return;
		const newValue = selectedLocations.filter((id) => id !== locationIdToRemove);
		const finalValue: Array<string> | 'all' = newValue.length === 0 ? 'all' : newValue;
		updateDraft('selectedLocations', finalValue);
	};

	const getLocationButtonText = () => {
		if (selectedLocations === 'all') return 'All Locations';
		const selectedLocationNames = selectedLocations
			.map((id) => locations.find((l) => l._id === id)?.title)
			.filter(Boolean) as string[];
		if (selectedLocationNames.length === 0) return 'Select Locations';
		if (selectedLocationNames.length === 1) return selectedLocationNames[0];
		if (selectedLocationNames.length === 2) return selectedLocationNames.join(', ');
		const firstLocation = selectedLocationNames[0];
		const remainingCount = selectedLocationNames.length - 1;
		return `${firstLocation} +${remainingCount} more`;
	};

	const getLocationTooltipLabel = () => {
		if (selectedLocations === 'all' || !Array.isArray(selectedLocations) || selectedLocations.length <= 2) {
			return undefined;
		}
		return selectedLocations
			.map((id) => locations.find((l) => l._id === id)?.title)
			.filter(Boolean)
			.join(', ');
	};

	const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
	const hasSelectedLocation = selectedLocations === 'all' || selectedLocations.length > 0;
	const shouldLockLocationToAll = locations.length === 0;

	useEffect(() => {
		if (shouldLockLocationToAll && selectedLocations !== 'all') {
			updateDraft('selectedLocations', 'all');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldLockLocationToAll, selectedLocations]);

	useEffect(() => {
		if (isInvoicingOrg && position !== 'invoicing') {
			updateDraft('position', 'invoicing');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInvoicingOrg, position]);

	const handleSendOtp = () => {
		const normalizedEmail = email.trim().toLowerCase();

		if (password !== confirmPassword) {
			toast({ title: 'Error', description: 'Passwords do not match', status: 'error', duration: 3000, isClosable: true });
			return;
		}
		if (!firstName.trim() || !lastName.trim() || !normalizedEmail || !username.trim() || !password || !confirmPassword || (!isInvoicingOrg && !position) || !hasSelectedLocation) {
			toast({ title: 'Missing Information', description: 'Please fill in all required fields before sending OTP.', status: 'warning', duration: 3000, isClosable: true });
			return;
		}

		sendOTP.mutateAsync({ email: normalizedEmail, accountNeeded: false })
			.then(() => {
				updateDraft('email', normalizedEmail);
				toast({ title: 'OTP Sent', description: `A verification code has been sent to ${normalizedEmail}.`, status: 'success', duration: 5000, isClosable: true });
				updateDraft('isOtpSent', true);
			})
			.catch((error) => {
				toast({ title: 'Failed to Send OTP', description: error.response?.data?.error || error.response?.data?.message || 'Could not send OTP. The email might already be registered or an error occurred.', status: 'error', duration: 5000, isClosable: true });
			});
	};

	const handleVerifyOtp = () => {
		if (!otp) {
			toast({ title: 'Missing OTP', description: 'Please enter the OTP code.', status: 'warning', duration: 3000, isClosable: true });
			return;
		}
		verifyOTP.mutateAsync({ email, otp })
			.then(() => {
				toast({ title: 'Email Verified', description: 'Your email has been successfully verified.', status: 'success', duration: 3000, isClosable: true });
				// If the privacy policy is already agreed, go straight to account creation without showing "Final Step"
				if (termsAgreed) {
					if (!isInvoicingOrg && !position) {
						toast({ title: 'Missing Information', description: 'Please select your position.', status: 'warning', duration: 3000, isClosable: true });
						updateDraft('isEmailVerified', true);
						return;
					}
					const effectivePosition: Role = isInvoicingOrg ? 'invoicing' : (position as Role);
					submitAccountCreation.mutateAsync({
						email,
						username: username.trim() || email.split('@')[0],
						password,
						lastName,
						firstName,
						locationId: selectedLocations,
						position: effectivePosition,
						organizationId,
					})
						.then((res) => {
							if (res.data.sessionId) {
								toast({ title: 'Success', description: 'Account created successfully!', status: 'success', duration: 3000, isClosable: true });
								finishSignupAndRouteAfterLogin(res.data.sessionId as string);
							} else {
								updateDraft('isEmailVerified', true);
								toast({ title: res.data.error || res.data.message || 'Could not create account. Please try again.', status: 'error', duration: 5000, isClosable: true });
							}
						})
						.catch((error: unknown) => {
							updateDraft('isEmailVerified', true);
							const serverMessage = error instanceof AxiosError
								? error.response?.data?.error || error.response?.data?.message
								: undefined;
							toast({ title: serverMessage || 'An unexpected error occurred.', status: 'error', duration: 5000, isClosable: true });
						});
				} else {
					// Privacy policy not yet agreed — show the final step to collect it
					updateDraft('isEmailVerified', true);
				}
			})
			.catch((error: unknown) => {
				toast({ title: 'OTP Verification Failed', description: error instanceof AxiosError ? error.response?.data?.message || 'Invalid or expired OTP. Please try again.' : 'Invalid or expired OTP. Please try again.', status: 'error', duration: 5000, isClosable: true });
			});
	};

	const handleFinalSubmit = () => {
		if (!isInvoicingOrg && !position) {
			toast({ title: 'Missing Information', description: 'Please select your position.', status: 'warning', duration: 3000, isClosable: true });
			return;
		}
		const effectivePosition: Role = isInvoicingOrg ? 'invoicing' : (position as Role);
		submitAccountCreation.mutateAsync({
			email,
			username: username.trim() || email.split('@')[0],
			password,
			lastName,
			firstName,
			locationId: selectedLocations,
			position: effectivePosition,
			organizationId
		})
			.then(res => {
				if (res.data.sessionId) {
					toast({ title: 'Success', description: 'Account created successfully!', status: 'success', duration: 3000, isClosable: true });
					finishSignupAndRouteAfterLogin(res.data.sessionId as string);
				} else {
					toast({ title: res.data.error || res.data.message || 'Could not create account. Please try again.', status: 'error', duration: 5000, isClosable: true });
				}
			})
			.catch((error) => {
				const serverMessage = error?.response?.data?.error || error?.response?.data?.message;
				toast({ title: serverMessage || 'An unexpected error occurred.', status: 'error', duration: 5000, isClosable: true });
			});
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!isOtpSent) {
			handleSendOtp();
		} else if (!isEmailVerified) {
			handleVerifyOtp();
		} else {
			handleFinalSubmit();
		}
	};

	return (
		<>
			<Center>
				<VStack spacing={4} as="form" onSubmit={handleSubmit} noValidate align="start" width="100%">
					<VStack spacing={6} align="center" width="100%">
						<HStack spacing={4} width="100%">
							<FormControl isRequired>
								<FormLabel>First Name</FormLabel>
								<Input
									type="text"
									value={firstName}
									onChange={(e) => updateDraft('firstName', e.target.value)}
									placeholder="First Name"
								/>
							</FormControl>
							<FormControl isRequired>
								<FormLabel>Last Name</FormLabel>
								<Input
									type="text"
									value={lastName}
									onChange={(e) => updateDraft('lastName', e.target.value)}
									placeholder="Last Name"
								/>
							</FormControl>
						</HStack>

						<HStack spacing={4} width="100%" align="flex-start">
							<FormControl isRequired>
								<FormLabel>Position</FormLabel>
								{isITInvite ? (
									<Select value="it" isDisabled>
										<option value="it">IT</option>
									</Select>
								) : isInvoicingOrg ? (
									<Select value="invoicing" placeholder="Select your position" isDisabled>
										<option value="invoicing">Invoicing</option>
									</Select>
								) : (
									<Select
										value={position}
										onChange={(e) => updateDraft('position', (e.target.value || '') as Role | '')}
										placeholder="Select your position"
									>
										{organizationMode === 'inventory' ? (
											getInvitePositionOptions({ inventoryOrg: true }).map((option) => (
												<option key={option.value} value={option.value}>{option.label}</option>
											))
										) : (
											<>
												<option value="executive">Executive</option>
												<option value="central-parts">Central Parts</option>
												<option value="parts-manager">Parts Manager</option>
												<option value="gm">General Manager</option>
												<option value="estimator">Estimator</option>
												<option value="it">IT</option>
												<option value="invoicing">Invoicing</option>
												<option value="technician">Technician</option>
												<option value="other">Other</option>
											</>
										)}
									</Select>
								)}
							</FormControl>
							<FormControl isRequired>
								<FormLabel>Location</FormLabel>
								{shouldLockLocationToAll ? (
									<Input
										type="text"
										value="All Locations"
										isReadOnly
										bg="gray.100"
										cursor="not-allowed"
									/>
								) : (
									<Popover
										placement="bottom-end"
										isOpen={locationPopoverOpen}
										onOpen={() => setLocationPopoverOpen(true)}
										onClose={() => setLocationPopoverOpen(false)}
									>
										<PopoverTrigger>
											<Button
												type="button"
												ref={locationTriggerRef}
												variant="outline"
												w="100%"
												h="40px"
												bg="white"
												borderColor="gray.300"
												borderRadius="md"
												fontWeight="normal"
												textAlign="left"
												justifyContent="flex-start"
												p={0}
												_hover={{ borderColor: 'gray.400' }}
											>
												<HStack w="100%" px={4} py={2} spacing={2}>
													<Box flex={1} minW={0}>
														{selectedLocations === 'all' ? (
															<Text noOfLines={1} wordBreak="break-word" fontSize="sm">{getLocationButtonText()}</Text>
														) : (() => {
															const selectedLocationNames = selectedLocations
																.map((id) => locations.find((l) => l._id === id)?.title)
																.filter(Boolean) as string[];

															if (selectedLocationNames.length <= 2) {
																return <Text noOfLines={1} wordBreak="break-word" fontSize="sm">{getLocationButtonText()}</Text>;
															}

															const firstLocation = selectedLocationNames[0];
															const remainingCount = selectedLocationNames.length - 1;
															const tooltipLabel = getLocationTooltipLabel();
															return (
																<HStack spacing={1} align="baseline" flexWrap="wrap">
																	<Text noOfLines={1} wordBreak="break-word" color="gray.700" fontSize="sm">{firstLocation}</Text>
																	<Tooltip label={tooltipLabel} placement="bottom" hasArrow>
																		<Text cursor="pointer" color="#0071E3" fontWeight="medium" whiteSpace="nowrap" fontSize="sm">
																			+{remainingCount} more
																		</Text>
																	</Tooltip>
																</HStack>
															);
														})()}
													</Box>
												</HStack>
											</Button>
										</PopoverTrigger>
										<PopoverContent w={typeof locationPopoverWidth === 'number' ? `${locationPopoverWidth}px` : locationPopoverWidth} maxH="500px" overflowY="auto">
											<PopoverArrow />
											<PopoverBody>
												<VStack align="stretch" spacing={2}>
													<Checkbox
														isChecked={selectedLocations === 'all'}
														onChange={handleAllLocationsChange}
													>
														<Text fontWeight="semibold">All Locations</Text>
													</Checkbox>

													{selectedLocations !== 'all' && Array.isArray(selectedLocations) && selectedLocations.length > 0 && (
														<Box bg="gray.100" p={2} borderRadius="md" maxH="180px" overflowY="auto">
															<Wrap spacing={2}>
																{selectedLocations.map((id) => {
																	const loc = locations.find((l) => l._id === id);
																	if (!loc) return null;
																	return (
																		<WrapItem key={id}>
																			<Tag size="sm" borderRadius="full" variant="solid" colorScheme="blue">
																				<TagLabel>{loc.title}</TagLabel>
																				<TagCloseButton onClick={() => handleRemoveLocation(id)} />
																			</Tag>
																		</WrapItem>
																	);
																})}
															</Wrap>
														</Box>
													)}

													<Divider borderColor="gray.300" />
													<InputGroup size="sm">
														<InputLeftElement pointerEvents="none">
															<SearchIcon color="gray.400" />
														</InputLeftElement>
														<Input
															placeholder="Search locations..."
															value={locationSearch}
															onChange={(e) => setLocationSearch(e.target.value)}
															bg="white"
															borderColor="gray.300"
														/>
													</InputGroup>
													<CheckboxGroup
														value={Array.isArray(selectedLocations) ? selectedLocations : []}
														onChange={handleIndividualLocationsChange}
													>
														<VStack align="stretch" spacing={2}>
															{locations
																.filter((l) => l._id !== 'all')
																.filter((l) =>
																	locationSearch === '' ||
																	l.title.toLowerCase().includes(locationSearch.toLowerCase())
																)
																.map((location) => (
																	<Checkbox key={location._id} value={location._id}>
																		{location.title}
																	</Checkbox>
																))}
														</VStack>
													</CheckboxGroup>
												</VStack>
											</PopoverBody>
										</PopoverContent>
									</Popover>
								)}
							</FormControl>
						</HStack>

						<HStack spacing={4} width="100%" >
							<FormControl>
								<FormLabel>Phone Number</FormLabel>
								<Input
									type="tel"
									value={phoneNumber}
									onChange={(e) => updateDraft('phoneNumber', formatPhoneNumber(e.target.value))}
									placeholder="+1 (555) 123-4567"
								/>
							</FormControl>
							<FormControl isRequired>
								<FormLabel>Email</FormLabel>
								<Input
									type="email"
									value={email}
									onChange={(e) => updateDraft('email', e.target.value)}
									onBlur={(e) => {
										if (!username.trim()) {
											updateDraft('username', e.target.value.split('@')[0].trim());
										}
									}}
									placeholder="Enter your email"
									isDisabled={(isITInvite && !!email) || isOtpSent}
								/>
							</FormControl>
						</HStack>

						<HStack spacing={4} width="100%">
							<FormControl isRequired>
								<FormLabel>Username</FormLabel>
								<Input
									type="text"
									value={username}
									onChange={(e) => updateDraft('username', e.target.value)}
									placeholder="Enter your username"
								/>
							</FormControl>
						</HStack>

						<HStack spacing={4} width="100%">
							<FormControl isRequired>
								<FormLabel>Password</FormLabel>
								<InputGroup>
									<Input
										type={showPassword ? 'text' : 'password'}
										value={password}
										onChange={(e) => updateDraft('password', e.target.value)}
										placeholder="Enter your password"
									/>
									<InputRightElement>
										<Icon
											as={showPassword ? FaEyeSlash as React.ElementType : FaEye as React.ElementType}
											onClick={() => setShowPassword(!showPassword)}
											cursor="pointer"
										/>
									</InputRightElement>
								</InputGroup>
							</FormControl>
							<FormControl isRequired>
								<FormLabel>Confirm Password</FormLabel>
								<InputGroup>
									<Input
										type={showConfirmPassword ? 'text' : 'password'}
										value={confirmPassword}
										onChange={(e) => updateDraft('confirmPassword', e.target.value)}
										placeholder="Confirm your password"
									/>
									<InputRightElement>
										<Icon
											as={showConfirmPassword ? FaEyeSlash as React.ElementType : FaEye as React.ElementType}
											onClick={() => setShowConfirmPassword(!showConfirmPassword)}
											cursor="pointer"
										/>
									</InputRightElement>
								</InputGroup>
							</FormControl>
						</HStack>

						{isOtpSent && !isEmailVerified && (
							<>
								<FormControl isRequired width="100%">
									<FormLabel>Verification Code (OTP)</FormLabel>
									<Input
										type="text"
										value={otp}
										onChange={(e) => updateDraft('otp', e.target.value)}
										placeholder="Enter the 6-digit code"
										maxLength={6}
										isDisabled={verifyOTP.isPending}
									/>
								</FormControl>
								<Text fontSize="xs" color="gray.500" textAlign="center" width="100%">
									Didn't receive the code or entered the wrong email?{' '}
									<Link
										color="blue.500"
										cursor="pointer"
										fontWeight="semibold"
										onClick={() => {
											updateDraft('isOtpSent', false);
											updateDraft('otp', '');
										}}
									>
										Change email & resend
									</Link>
								</Text>
								<FormControl width="100%">
									<Checkbox
										isChecked={termsAgreed}
										onChange={(e) => updateDraft('termsAgreed', e.target.checked)}
										colorScheme="blue"
									>
										<Text fontSize="sm">
											I agree to the{' '}
											<Link
												color="blue.500"
												onClick={() => setIsPrivacyModalOpen(true)}
												cursor="pointer"
												textDecoration="underline"
											>
												Privacy Policy
											</Link>
										</Text>
									</Checkbox>
								</FormControl>
							</>
						)}

						{isEmailVerified && (
							<FormControl width="100%">
								<Checkbox
									isChecked={termsAgreed}
									onChange={(e) => updateDraft('termsAgreed', e.target.checked)}
									colorScheme="blue"
								>
									<Text fontSize="sm">
										I agree to the{' '}
										<Link
											color="blue.500"
											onClick={() => setIsPrivacyModalOpen(true)}
											cursor="pointer"
											textDecoration="underline"
										>
											Privacy Policy
										</Link>
									</Text>
								</Checkbox>
							</FormControl>
						)}
					</VStack>
					<Button
						bg="#0071E3"
						color="white"
						_hover={{ bg: '#3141a5' }}
						width="100%"
						type="submit"
						size="sm"
						isLoading={sendOTP.isPending || verifyOTP.isPending || submitAccountCreation.isPending}
						loadingText={
							submitAccountCreation.isPending ? 'Creating account...' : verifyOTP.isPending ? 'Verifying...' : 'Sending...'
						}
						isDisabled={(isOtpSent && !isEmailVerified) ? !otp : (isEmailVerified && (!termsAgreed || !position))}
					>
						{isEmailVerified ? 'Create Account' : isOtpSent ? 'Verify Email' : 'Send Verification Code'}
					</Button>
				</VStack>
			</Center>
			<PrivacyPolicy
				isOpen={isPrivacyModalOpen}
				onClose={() => setIsPrivacyModalOpen(false)}
			/>
		</>
	);
};

export default CreateAccount;